import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function sessionId() {
  let sid = localStorage.getItem("brilla_chat_session");
  if (!sid) {
    sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("brilla_chat_session", sid);
  }
  return sid;
}

export default function AskBrilla() {
  const { t } = useLang();
  const [sid] = useState(sessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/chat/${sid}`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [sid]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sid, message: msg });
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const suggestions = t("ask.suggestions");

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-6 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-11 w-11 rounded-2xl bg-primary flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-primary-foreground" />
          </span>
          <div>
            <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("ask.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("ask.subtitle")}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 rounded-3xl border border-border bg-card p-5" data-testid="chat-window">
          {messages.length === 0 && !loading && (
            <div className="space-y-4 py-6">
              <p className="text-muted-foreground text-center">{t("ask.subtitle")}</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`suggestion-${i}`}
                    className="text-left rounded-xl border border-border px-4 py-3 text-sm hover:border-primary/40 hover:bg-muted/50 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-secondary text-secondary-foreground" : "bg-muted text-foreground"}`}
                data-testid={`msg-${m.role}-${i}`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-muted px-4 py-3 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("ask.thinking")}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2" data-testid="chat-form">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("ask.placeholder")} className="h-12 rounded-xl" data-testid="chat-input" />
          <Button type="submit" disabled={loading || !input.trim()} className="h-12 px-5 rounded-xl bg-secondary hover:bg-secondary/90" data-testid="chat-send">
            <Send className="h-5 w-5" />
          </Button>
        </form>
      </main>
    </div>
  );
}
